import React, { useEffect, useState } from "react";
import { Moon, Heart, Activity, Thermometer, Footprints, Flame, Wind, Sparkles, Gauge, Waves } from "lucide-react";
import { fitnessAgeFromVO2max, ageFromDob } from "./fitnessAge";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchWearableMetrics,
  formatSleepDuration,
  summarizeForToday,
  fetchWearableStress,
  stressLabelFor,
  type WearableTodaySummary,
  type WearableStressSummary,
} from "./wearableMetrics";
import {
  computeSymptomRiskForecast,
  TEMP_DELTA_C_THRESHOLD,
  BASELINE_WINDOW_NIGHTS,
  type SymptomRiskForecast,
  type SignalSnapshot,
} from "./symptomRiskForecast";
import { WEARABLE_LABELS, type WearableProvider } from "@/lib/wearables-shared";
import { parseLocalDateKey } from "./dailyLogs";

const PLUM = "#4B1F4F";
const ROSE = "#FF5C7A";
const ROSE_LIGHT = "#FFC1CD";
const CREAM = "#FAF7F5";
const LINE = "#E8D5DA";
const MUTED = "#6E4B71";

function fmtAgo(dk: string | null): string {
  if (!dk) return "";
  const d = parseLocalDateKey(dk);
  if (!d) return "";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.floor((today.getTime() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

/**
 * Today's Wearable Snapshot — shows latest sleep, resting HR, 7-day HRV
 * average, and latest skin-temperature reading from connected wearables.
 * Wellness tone — no clinical thresholds.
 */
export function TodaysWearableCard({ stage, dob }: { stage?: string | null; dob?: string | null } = {}) {
  const isPostmeno = stage === "Postmenopause";
  const [summary, setSummary] = useState<WearableTodaySummary | null>(null);
  const [forecast, setForecast] = useState<SymptomRiskForecast | null>(null);
  const [stress, setStress] = useState<WearableStressSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const uid = data?.user?.id;
        if (!uid) { if (!cancelled) setLoading(false); return; }
        // Pull enough rows to cover the baseline window the forecast uses, so
        // both the tile and the Outlook card derive skin-temp from one source.
        const rows = await fetchWearableMetrics(uid, BASELINE_WINDOW_NIGHTS + 7);
        const stressSummary = await fetchWearableStress(uid, 7);
        if (!cancelled) {
          setSummary(summarizeForToday(rows));
          setForecast(computeSymptomRiskForecast(rows));
          setStress(stressSummary);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!summary || !summary.hasAny) return null;

  const providerLabel = (summary.providers.length ? summary.providers : (summary.provider ? [summary.provider] : []))
    .map((p) => (WEARABLE_LABELS as any)[p as WearableProvider] ?? p)
    .join(" + ") || "Wearable";

  const tiles: { icon: React.ReactNode; label: string; value: string; sub: React.ReactNode }[] = [];

  if (summary.sleep.duration != null) {
    tiles.push({
      icon: <Moon size={15} color={ROSE} strokeWidth={2} />,
      label: "Sleep",
      value: formatSleepDuration(summary.sleep.duration),
      sub: summary.sleep.quality != null ? `Quality ${Math.round(summary.sleep.quality)}/100` : "Last night",
    });
  }
  if (summary.restingHr.value != null) {
    tiles.push({
      icon: <Heart size={15} color={ROSE} strokeWidth={2} />,
      label: "Resting HR",
      value: `${Math.round(summary.restingHr.value)} bpm`,
      sub: "Last night",
    });
  }
  // HRV: headline = LAST NIGHT's value. The "your usual" baseline shown here
  // MUST be the same baseline the forecast uses to compute "% above/below your
  // usual" (the 14-night median), so the tile and the Why? panel reconcile
  // arithmetically. Fall back to the 7-day average only when the forecast
  // baseline is not yet available.
  const hrvSnap = forecast?.signalSnapshots.find((s) => s.key === "hrv") ?? null;
  const hrvLastNight = hrvSnap?.lastNight ?? null;
  if (hrvLastNight != null) {
    const usual = hrvSnap?.baseline ?? null;
    tiles.push({
      icon: <Activity size={15} color={ROSE} strokeWidth={2} />,
      label: "HRV",
      value: `${Math.round(hrvLastNight)} ms`,
      sub: usual != null
        ? `Last night · your usual ${Math.round(usual)} ms`
        : "Last night",
    });
  } else if (summary.hrv7dAvg != null) {
    tiles.push({
      icon: <Activity size={15} color={ROSE} strokeWidth={2} />,
      label: "HRV (7-day avg)",
      value: `${Math.round(summary.hrv7dAvg)} ms`,
      sub: "Building last-night baseline",
    });
  }

  // Stress (derived from HRV) — READ-ONLY display of the value the trigger
  // engine already stored in daily_triggers via mapStressFromHrv(). Never
  // re-derive here. Only render when there's a real hrv_proxy reading.
  if (stress?.latest.value != null) {
    tiles.push({
      icon: <Gauge size={15} color={ROSE} strokeWidth={2} />,
      label: "Stress (derived from HRV)",
      value: `${stressLabelFor(stress.latest.value)} (${stress.latest.value}/4)`,
      sub: "Estimated from overnight HRV",
    });
  }

  if (summary.skinTemp.value != null) {
    // SINGLE SOURCE: prefer the skin-temp delta from the forecast snapshot —
    // it's what the "Today's Outlook" headline and "Why?" panel show, so all
    // three surfaces report the same number. Fall back to the raw stored
    // value (already a deviation) only when no baseline is available yet.
    const snap = forecast?.signalSnapshots.find((s) => s.key === "skin_temperature") ?? null;
    const deltaC = snap ? snap.delta : summary.skinTemp.value;
    const isDelta = Math.abs(deltaC) < 5;
    let status: string;
    if (!isDelta) {
      // Defensive: not actually a delta — show raw with a neutral status.
      status = `${deltaC.toFixed(1)}°C`;
    } else if (deltaC >= 0.5) {
      status = "Elevated";
    } else if (deltaC >= TEMP_DELTA_C_THRESHOLD) {
      status = "Slightly elevated";
    } else {
      status = "Normal for you";
    }
    const fDelta = deltaC * 1.8; // delta °F = delta °C × 1.8 (NO +32 offset)
    const sign = (n: number) => (n >= 0 ? "+" : "");
    const sub = isDelta
      ? `${sign(fDelta)}${fDelta.toFixed(1)}°F vs your baseline`
      : fmtAgo(summary.skinTemp.date);
    tiles.push({
      icon: <Thermometer size={15} color={ROSE} strokeWidth={2} />,
      label: "Skin temp",
      value: status,
      sub,
    });
  }

  if (summary.respiratoryRate.value != null) {
    tiles.push({
      icon: <Waves size={15} color={ROSE} strokeWidth={2} />,
      label: "Respiratory rate",
      value: `${summary.respiratoryRate.value.toFixed(1)} /min`,
      sub: "Last night",
    });
  }

  // VO2max + Fitness Age — postmeno only, when VO2max is available.
  if (isPostmeno && summary.vo2Max.value != null) {
    const vo2 = summary.vo2Max.value;
    tiles.push({
      icon: <Wind size={15} color={ROSE} strokeWidth={2} />,
      label: "VO₂max",
      value: `${vo2.toFixed(1)} ml/kg/min`,
      sub: `Cardio fitness · ${providerLabel}`,
    });

    const age = ageFromDob(dob);
    if (age == null) {
      tiles.push({
        icon: <Sparkles size={15} color={ROSE} strokeWidth={2} />,
        label: "Fitness age",
        value: "—",
        sub: "Add your birth year in Settings",
      });
    } else {
      const fa = fitnessAgeFromVO2max(vo2, age);
      if (fa) {
        const delta = fa.deltaYears;
        // positive = younger fitness age (good) → green; negative = older → amber
        const color = delta > 0 ? "#3F8F5C" : delta < 0 ? "#B5731F" : MUTED;
        const sign = delta > 0 ? "−" : delta < 0 ? "+" : "";
        const deltaLabel = delta === 0 ? "matches your age" : `${sign}${Math.abs(delta)} yrs vs your age`;
        tiles.push({
          icon: <Sparkles size={15} color={ROSE} strokeWidth={2} />,
          label: "Fitness age",
          value: `${fa.fitnessAge} yrs`,
          sub: <span style={{ color, fontWeight: 500 }}>{deltaLabel}</span>,
        });
      }
    }
  }

  const hasActivity =
    summary.yesterdayActivity.steps != null ||
    summary.yesterdayActivity.activeMinutes != null;
  if (!tiles.length && !hasActivity) return null;

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1.5px solid ${ROSE_LIGHT}`,
        borderRadius: 18,
        padding: "14px 14px 12px",
        marginBottom: 14,
        boxShadow: "0 8px 24px -18px rgba(74,25,66,0.3)",
      }}
      role="region"
      aria-label="Wearable snapshot"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 28, height: 28, borderRadius: "50%",
              background: CREAM, border: `1px solid ${LINE}`,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              flex: "0 0 auto",
            }}
            aria-hidden
          >
            <Activity size={15} color={ROSE} strokeWidth={2} />
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: PLUM }}>
            Wearable Snapshot
          </span>
        </div>
        <span
          style={{
            fontSize: 10.5, fontWeight: 500, color: MUTED,
            letterSpacing: "0.04em", textTransform: "uppercase",
            background: CREAM, border: `1px solid ${LINE}`,
            borderRadius: 999, padding: "2px 8px",
          }}
        >
          {providerLabel}
        </span>
      </div>

      <div
        style={{
          fontSize: 11, color: MUTED, marginBottom: 10, marginTop: -2,
          fontStyle: "italic",
        }}
      >
        From last night · synced this morning
      </div>


      {tiles.length > 0 && (
        <div className="km-metric-grid">
          {tiles.map((t, i) => (
            <div
              key={i}
              className="km-metric-tile"
              style={{
                background: CREAM,
                border: `1px solid ${LINE}`,
                borderRadius: 12,
                padding: "10px 10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                {t.icon}
                <span style={{ fontSize: 10.5, color: MUTED, fontWeight: 500 }}>{t.label}</span>
              </div>
              <div style={{ fontSize: 15, color: "#3A1A3E", fontWeight: 600, lineHeight: 1.2 }}>{t.value}</div>
              <div style={{ fontSize: 10.5, color: MUTED, marginTop: 2 }}>{t.sub}</div>
            </div>
          ))}
        </div>
      )}

      {tiles.length === 0 && hasActivity && (
        <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.45, marginBottom: 4 }}>
          Overnight metrics — sleep, HRV, resting heart rate — appear after your device syncs its first full night.
        </div>
      )}

      {(summary.yesterdayActivity.steps != null || summary.yesterdayActivity.activeMinutes != null) && (
        <>
          <div
            style={{
              fontSize: 11, color: MUTED, marginTop: 12, marginBottom: 6,
              fontStyle: "italic",
            }}
          >
            Yesterday's daytime activity
          </div>
          <div className="km-metric-grid">
            {summary.yesterdayActivity.steps != null && (
              <div className="km-metric-tile" style={{ background: CREAM, border: `1px solid ${LINE}`, borderRadius: 12, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <Footprints size={15} color={ROSE} strokeWidth={2} />
                  <span style={{ fontSize: 11, color: MUTED, fontWeight: 500 }}>Steps</span>
                </div>
                <div style={{ fontSize: 15, color: "#3A1A3E", fontWeight: 600, lineHeight: 1.2 }}>
                  {summary.yesterdayActivity.steps.toLocaleString()}
                </div>
                <div style={{ fontSize: 10.5, color: MUTED, marginTop: 2 }}>{(() => { const a = fmtAgo(summary.yesterdayActivity.date); return a ? a.charAt(0).toUpperCase() + a.slice(1) : ""; })()}</div>
              </div>
            )}
            {summary.yesterdayActivity.activeMinutes != null && (
              <div className="km-metric-tile" style={{ background: CREAM, border: `1px solid ${LINE}`, borderRadius: 12, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <Flame size={15} color={ROSE} strokeWidth={2} />
                  <span style={{ fontSize: 11, color: MUTED, fontWeight: 500 }}>Active minutes</span>
                </div>
                <div style={{ fontSize: 15, color: "#3A1A3E", fontWeight: 600, lineHeight: 1.2 }}>
                  {Math.round(summary.yesterdayActivity.activeMinutes)} min
                </div>
                <div style={{ fontSize: 10.5, color: MUTED, marginTop: 2 }}>{(() => { const a = fmtAgo(summary.yesterdayActivity.date); return a ? a.charAt(0).toUpperCase() + a.slice(1) : ""; })()}</div>
              </div>
            )}
          </div>
        </>
      )}

      <div
        style={{
          fontSize: 11, color: MUTED, fontStyle: "italic",
          lineHeight: 1.4, borderTop: `0.5px solid ${LINE}`,
          paddingTop: 8, marginTop: 10,
        }}
      >
        Wellness data from your wearable — not a medical diagnosis.
        {stress?.latest.value != null && (
          <div style={{ marginTop: 4 }}>
            Stress is estimated from overnight HRV vs your 7-day baseline — not a direct sensor reading.
          </div>
        )}
        {isPostmeno && summary.vo2Max.value != null && (
          <div style={{ marginTop: 4 }}>
            Fitness age is an estimate based on cardio fitness vs the typical woman your age.
          </div>
        )}
      </div>
    </div>
  );
}
